import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Modal } from "react-bootstrap";
import {
  GeoAlt,
  Globe2,
  Building,
  CheckCircleFill,
  ChevronRight,
  Folder,
  PencilSquare,
  XCircle,
  Sun,
  Moon,
  Palette,
  Fonts
} from "react-bootstrap-icons";
import { getProvincialInfo } from "../../utils/canadaFeedsConfig";
import { getMexicanStateInfo } from "../../utils/mexicoFeedsConfig";
import { getUKRegionInfo } from "../../utils/unitedKingdomFeedsConfig";
import { useTranslation } from 'react-i18next';
import {
  getThemePreferences,
  setAutoEnabled,
  selectTheme,
  resolveTheme,
  getAutoSide,
  MODES
} from "../../utils/themeUtils";


// ============ THEME CONFIGURATION ============
// Auto is NOT a theme -- it's a switch (see themeUtils). The concrete themes are
// grouped by side, because that grouping is the actual preference shape: the
// user always has one light pick and one dark pick, and Auto decides which is
// showing. Each column reads and writes its own key on the prefs object.
//
// Note the two Classics are different themes, not one theme in two modes:
// classic-light is the sky blue that matches govglance.org, classic is the
// original deep blue. THEME_SIDE in themeUtils is the source of truth for which
// side an id belongs to; adding a theme means adding it there and here.
const THEME_COLUMNS = [
  {
    side: MODES.LIGHT,
    prefKey: 'lightTheme',
    labelKey: 'settings:themeLight',
    labelFallback: 'Light',
    icon: <Sun size={14} />,
    options: [
      {
        id: 'classic-light',
        name: 'Classic',
        preview: {
          bg: 'linear-gradient(180deg, #f0f7ff 0%, #dbeafe 100%)',
          card: '#ffffff',
          text: '#1e293b',
          accent: '#2563eb',
          isGradient: true
        }
      },
      {
        id: 'comfort-light',
        name: 'Clean',
        preview: {
          bg: '#F5F3EF',
          card: '#FFFEFA',
          text: '#2D2D2D',
          accent: '#016cae'
        }
      }
    ]
  },
  {
    side: MODES.DARK,
    prefKey: 'darkTheme',
    labelKey: 'settings:themeDark',
    labelFallback: 'Dark',
    icon: <Moon size={14} />,
    options: [
      {
        id: 'classic',
        name: 'Classic',
        preview: {
          bg: '#002031',
          card: '#01385a',
          text: '#ffffff',
          accent: '#016cae'
        }
      },
      {
        id: 'comfort-dark',
        name: 'Clean',
        preview: {
          bg: '#121212',
          card: '#1E1E1E',
          text: '#E8E8E8',
          accent: '#81C7F5'
        }
      }
    ]
  }
];

// ============ FONT CONFIGURATION ============
const FONTS = {
  SYSTEM: 'system',
  VERDANA: 'verdana',
  ATKINSON: 'atkinson'
};

const FONT_INFO = {
  [FONTS.SYSTEM]: {
    name: 'Default',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  [FONTS.VERDANA]: {
    name: 'Verdana',
    fontFamily: 'Verdana, Geneva, sans-serif'
  },
  [FONTS.ATKINSON]: {
    name: 'Atkinson',
    fontFamily: '"Atkinson Hyperlegible", -apple-system, BlinkMacSystemFont, sans-serif'
  }
};

const ALL_FONTS = Object.keys(FONT_INFO);

// ============ LOCATION CONFIGURATION ============
const states = [
  { name: "Alabama", flag: "/alabamaFlag.png", key: "alabama" },
  { name: "Alaska", flag: "/alaskaFlag.png", key: "alaska" },
  { name: "Arizona", flag: "/arizonaFlag.png", key: "arizona" },
  { name: "Arkansas", flag: "/arkansasFlag.png", key: "arkansas" },
  { name: "California", flag: "/californiaFlag.png", key: "california" },
  { name: "Colorado", flag: "/coloradoFlag.png", key: "colorado" },
  { name: "Connecticut", flag: "/connecticutFlag.png", key: "connecticut" },
  { name: "Delaware", flag: "/delawareFlag.png", key: "delaware" },
  { name: "Florida", flag: "/floridaFlag.png", key: "florida" },
  { name: "Georgia", flag: "/georgiaFlag.png", key: "georgia" },
  { name: "Hawaii", flag: "/hawaiiFlag.png", key: "hawaii" },
  { name: "Idaho", flag: "/idahoFlag.png", key: "idaho" },
  { name: "Illinois", flag: "/illinoisFlag.png", key: "illinois" },
  { name: "Indiana", flag: "/indianaFlag.png", key: "indiana" },
  { name: "Iowa", flag: "/iowaFlag.png", key: "iowa" },
  { name: "Kansas", flag: "/kansasFlag.png", key: "kansas" },
  { name: "Kentucky", flag: "/kentuckyFlag.png", key: "kentucky" },
  { name: "Louisiana", flag: "/louisianaFlag.png", key: "louisiana" },
  { name: "Maine", flag: "/maineFlag.png", key: "maine" },
  { name: "Maryland", flag: "/marylandFlag.png", key: "maryland" },
  { name: "Massachusetts", flag: "/massachusettsFlag.png", key: "massachusetts" },
  { name: "Michigan", flag: "/michiganFlag.png", key: "michigan" },
  { name: "Minnesota", flag: "/minnesotaFlag.png", key: "minnesota" },
  { name: "Mississippi", flag: "/mississippiFlag.png", key: "mississippi" },
  { name: "Missouri", flag: "/missouriFlag.png", key: "missouri" },
  { name: "Montana", flag: "/montanaFlag.png", key: "montana" },
  { name: "Nebraska", flag: "/nebraskaFlag.png", key: "nebraska" },
  { name: "Nevada", flag: "/nevadaFlag.png", key: "nevada" },
  { name: "New Hampshire", flag: "/newHampshireFlag.png", key: "newhampshire" },
  { name: "New Jersey", flag: "/newJerseyFlag.png", key: "newjersey" },
  { name: "New Mexico", flag: "/newMexicoFlag.png", key: "newmexico" },
  { name: "New York", flag: "/newYorkFlag.png", key: "newyork" },
  { name: "North Carolina", flag: "/northCarolinaFlag.png", key: "northcarolina" },
  { name: "North Dakota", flag: "/northDakotaFlag.png", key: "northdakota" },
  { name: "Ohio", flag: "/ohioFlag.png", key: "ohio" },
  { name: "Oklahoma", flag: "/oklahomaFlag.png", key: "oklahoma" },
  { name: "Oregon", flag: "/oregonFlag.png", key: "oregon" },
  { name: "Pennsylvania", flag: "/pennsylvaniaFlag.png", key: "pennsylvania" },
  { name: "Rhode Island", flag: "/rhodeIslandFlag.png", key: "rhodeisland" },
  { name: "South Carolina", flag: "/southCarolinaFlag.png", key: "southcarolina" },
  { name: "South Dakota", flag: "/southDakotaFlag.png", key: "southdakota" },
  { name: "Tennessee", flag: "/tennesseeFlag.png", key: "tennessee" },
  { name: "Texas", flag: "/texasFlag.png", key: "texas" },
  { name: "Utah", flag: "/utahFlag.png", key: "utah" },
  { name: "Vermont", flag: "/vermontFlag.png", key: "vermont" },
  { name: "Virginia", flag: "/virginiaFlag.png", key: "virginia" },
  { name: "Washington", flag: "/washingtonFlag.png", key: "washington" },
  { name: "West Virginia", flag: "/westVirginiaFlag.png", key: "westvirginia" },
  { name: "Wisconsin", flag: "/wisconsinFlag.png", key: "wisconsin" },
  { name: "Wyoming", flag: "/wyomingFlag.png", key: "wyoming" }
];

const PROVINCE_KEYS = [
  "alberta", "british_columbia", "manitoba", "new_brunswick",
  "newfoundland_labrador", "northwest_territories", "nova_scotia",
  "nunavut", "ontario", "prince_edward_island", "quebec", "saskatchewan", "yukon"
];

const MEXICAN_STATE_KEYS = [
  "aguascalientes", "bajacalifornia", "bajacaliforniasur", "campeche",
  "chiapas", "chihuahua", "coahuila", "colima", "durango", "guanajuato",
  "guerrero", "hidalgo", "jalisco", "mexicocity", "mexicostate", "michoacan",
  "morelos", "nayarit", "nuevoleon", "oaxaca", "puebla", "queretaro",
  "quintanaroo", "sanluispotosi", "sinaloa", "sonora", "tabasco",
  "tamaulipas", "tlaxcala", "veracruz", "yucatan", "zacatecas"
];

const UK_REGION_KEYS = ["england", "scotland", "wales", "northernireland"];

const FULL_SUPPORT_COUNTRIES = ['us', 'ca', 'mx', 'uk'];

const ALL_COUNTRIES = [
  { code: 'us', name: 'United States', flag: '🇺🇸' },
  { code: 'ca', name: 'Canada', flag: '🇨🇦' },
  { code: 'mx', name: 'Mexico', flag: '🇲🇽' },
  { code: 'uk', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'australia', name: 'Australia', flag: '🇦🇺' },
  { code: 'france', name: 'France', flag: '🇫🇷' },
  { code: 'italy', name: 'Italy', flag: '🇮🇹' },
  { code: 'japan', name: 'Japan', flag: '🇯🇵' },
  { code: 'south-africa', name: 'South Africa', flag: '🇿🇦' },
  { code: 'ukraine', name: 'Ukraine', flag: '🇺🇦' }
];

// ============ LANGUAGE CONFIGURATION ============
const LANGUAGES = {
  EN: 'en',
  ES: 'es',
  FR: 'fr'
};

// Language names are intentionally NOT translated - a language picker
// always shows each language in its own name.
const LANGUAGE_INFO = {
  [LANGUAGES.EN]: { name: 'English' },
  [LANGUAGES.ES]: { name: 'Español' },
  [LANGUAGES.FR]: { name: 'Français' }
};

const ALL_LANGUAGES = Object.keys(LANGUAGE_INFO);

// ============ MAIN COMPONENT ============
const PreferencesPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('settings');

  // Theme state. One object rather than a single selected id: the user has a
  // light pick, a dark pick, and a switch saying whether the OS chooses between
  // them. themeUtils owns the storage and the DOM; this is just a mirror for
  // rendering, refreshed from whatever the mutation returns.
  const [themePrefs, setThemePrefs] = useState(getThemePreferences);
  const activeTheme = resolveTheme(themePrefs);
  // getAutoSide, not getThemeSide: this is the light-or-dark question, and
  // Classic paints under its own data-mode while still counting as dark here.
  const activeSide = getAutoSide(activeTheme);

  // Font state
  const [selectedFont, setSelectedFont] = useState(FONTS.SYSTEM);

  // Language state
  const [selectedLanguage, setSelectedLanguage] = useState(LANGUAGES.EN);

  // Location state
  const [homeCountry, setHomeCountry] = useState(null);
  const [homeState, setHomeState] = useState(null);
  const [homeProvince, setHomeProvince] = useState(null);
  const [homeMexicanState, setHomeMexicanState] = useState(null);
  const [homeUKRegion, setHomeUKRegion] = useState(null);
  const [homeCity, setHomeCity] = useState(null);
  
  // Modal states
  const [countryModalOpen, setCountryModalOpen] = useState(false);
  const [regionModalOpen, setRegionModalOpen] = useState(false);
  const [cityModalOpen, setCityModalOpen] = useState(false);
  
  // City search state
  const [citySearchQuery, setCitySearchQuery] = useState("");
  const [citySearchResults, setCitySearchResults] = useState([]);
  const [searchingCities, setSearchingCities] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const searchTimeoutRef = useRef(null);
  
  // Success message
  const [successMessage, setSuccessMessage] = useState(null);

  // Build provinces from config
  const provinces = useMemo(() => {
    return PROVINCE_KEYS.map((k) => {
      const info = getProvincialInfo(k);
      if (!info) return null;
      return {
        key: k,
        name: info.fullName,
        flag: info.flagImage,
        code: info.code
      };
    }).filter(Boolean);
  }, []);

  // Build Mexican states from config
  const mexicanStates = useMemo(() => {
    return MEXICAN_STATE_KEYS.map((k) => {
      const info = getMexicanStateInfo(k);
      if (!info) return null;
      return {
        key: k,
        name: info.fullName,
        flag: info.flagImage,
        code: info.code
      };
    }).filter(Boolean);
  }, []);

  // Build UK regions from config
  const ukRegions = useMemo(() => {
    return UK_REGION_KEYS.map((k) => {
      const info = getUKRegionInfo(k);
      if (!info) return null;
      return {
        key: k,
        name: info.fullName,
        flag: info.flagImage,
        code: info.code
      };
    }).filter(Boolean);
  }, []);

  // Load saved values on mount. Theme is absent here on purpose: App.js applies
  // it before React renders and watchSystemTheme keeps it current, so this page
  // only reads the preference (in useState above) rather than re-applying it.
  useEffect(() => {
    const savedFont = localStorage.getItem('font');
    if (savedFont && Object.values(FONTS).includes(savedFont)) {
      setSelectedFont(savedFont);
    } else {
      setSelectedFont(FONTS.SYSTEM);
    }

    const savedLanguage = localStorage.getItem('gg_lang');
    if (savedLanguage && Object.values(LANGUAGES).includes(savedLanguage)) {
      setSelectedLanguage(savedLanguage);
    } else {
      setSelectedLanguage(i18n.language);
    }
    
    setHomeCountry(localStorage.getItem("homeCountry"));
    setHomeState(localStorage.getItem("homeState"));
    setHomeProvince(localStorage.getItem("homeProvince"));
    setHomeMexicanState(localStorage.getItem("homeMexicanState"));
    setHomeUKRegion(localStorage.getItem("homeUKRegion"));
    setHomeCity(localStorage.getItem("homeCity"));
  }, []);

  // Apply font preference
  useEffect(() => {
    document.documentElement.setAttribute('data-font', selectedFont);
  }, [selectedFont]);

  // Show success message temporarily
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // ============ THEME HANDLERS ============
  // Both mutations return the new preference object, so state and storage can't
  // drift apart. Neither touches the DOM here -- themeUtils does that.
  const handleAutoToggle = (enabled) => {
    setThemePrefs(setAutoEnabled(enabled));
    showSuccess(t('settings:themeUpdated', 'Theme updated!'));
  };

  // With Auto on this only changes that side's standing pick -- the device still
  // decides which side is showing, so picking the off-side theme won't visibly
  // change anything. With Auto off, selectTheme also switches to that side.
  const handleThemeSelect = (themeId) => {
    setThemePrefs(selectTheme(themeId));
    showSuccess(t('settings:themeUpdated', 'Theme updated!'));
  };

  // ============ FONT HANDLERS ============
  const handleFontChange = (fontKey) => {
    setSelectedFont(fontKey);
    localStorage.setItem('font', fontKey);
    document.documentElement.setAttribute('data-font', fontKey);
    showSuccess(t('settings:fontUpdated', 'Font updated!'));
  };

  // ============ LANGUAGE HANDLERS ============
  const handleLanguageChange = (langKey) => {
    setSelectedLanguage(langKey);
    localStorage.setItem('gg_lang', langKey);
    i18n.changeLanguage(langKey);
    showSuccess(t('settings:languageUpdated', 'Language updated!'));
  };

  // ============ LOCATION HANDLERS ============
  const searchCities = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setCitySearchResults([]);
      setSearchError(null);
      setShowCityDropdown(false);
      return;
    }

    setSearchingCities(true);
    setSearchError(null);

    try {
      const response = await fetch(
        `https://search.govglance.org/api/geocode?search=${encodeURIComponent(query)}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to search cities');
      }
      
      const data = await response.json();
      const validTypes = ['City', 'Town', 'Village', 'Borough', 'Township', 'Municipality', 'Locality'];
      const filteredResults = (data.results || []).filter(result => {
        return validTypes.includes(result.type);
      });
      
      setCitySearchResults(filteredResults);
      setShowCityDropdown(filteredResults.length > 0);
    } catch (error) {
      console.error('City search error:', error);
      setSearchError(t('settings:cityError', 'Failed to search cities. Please try again.'));
      setCitySearchResults([]);
      setShowCityDropdown(false);
    } finally {
      setSearchingCities(false);
    }
  }, [t]);

  const handleCitySearchChange = useCallback((e) => {
    const query = e.target.value;
    setCitySearchQuery(query);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchCities(query);
    }, 300);
  }, [searchCities]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleCountrySelect = (countryCode) => {
    localStorage.setItem("homeCountry", countryCode);
    setHomeCountry(countryCode);
    
    if (countryCode !== 'us') {
      localStorage.removeItem("homeState");
      localStorage.removeItem("homeCity");
      localStorage.removeItem("homeCityData");
      setHomeState(null);
      setHomeCity(null);
    }
    if (countryCode !== 'ca') {
      localStorage.removeItem("homeProvince");
      setHomeProvince(null);
    }
    if (countryCode !== 'mx') {
      localStorage.removeItem("homeMexicanState");
      setHomeMexicanState(null);
    }
    if (countryCode !== 'uk') {
      localStorage.removeItem("homeUKRegion");
      setHomeUKRegion(null);
    }
    
    setCountryModalOpen(false);
    showSuccess(t('settings:countryUpdated', 'Country updated!'));
    window.dispatchEvent(new Event("storage"));
  };

  const handleStateSelect = (stateKey) => {
    localStorage.setItem("homeState", stateKey);
    localStorage.setItem(stateKey, "true");
    setHomeState(stateKey);
    
    localStorage.removeItem("homeCity");
    localStorage.removeItem("homeCityData");
    setHomeCity(null);
    
    setRegionModalOpen(false);
    showSuccess(t('settings:stateUpdated', 'State updated!'));
    window.dispatchEvent(new Event("storage"));
  };

  const handleProvinceSelect = (provinceKey) => {
    const info = getProvincialInfo(provinceKey);
    if (!info) return;
    
    localStorage.setItem("homeProvince", provinceKey);
    localStorage.setItem(`${info.code}Updates`, "true");
    setHomeProvince(provinceKey);
    
    setRegionModalOpen(false);
    showSuccess(t('settings:provinceUpdated', 'Province/Territory updated!'));
    window.dispatchEvent(new Event("storage"));
  };

  const handleMexicanStateSelect = (stateKey) => {
    const info = getMexicanStateInfo(stateKey);
    if (!info) return;
    
    localStorage.setItem("homeMexicanState", stateKey);
    localStorage.setItem(`${info.code}Updates`, "true");
    setHomeMexicanState(stateKey);
    
    setRegionModalOpen(false);
    showSuccess(t('settings:stateUpdated', 'State updated!'));
    window.dispatchEvent(new Event("storage"));
  };

  const handleUKRegionSelect = (regionKey) => {
    const info = getUKRegionInfo(regionKey);
    if (!info) return;
    
    localStorage.setItem("homeUKRegion", regionKey);
    localStorage.setItem(`${info.code}Updates`, "true");
    setHomeUKRegion(regionKey);
    
    setRegionModalOpen(false);
    showSuccess(t('settings:regionUpdated', 'Region updated!'));
    window.dispatchEvent(new Event("storage"));
  };

  const handleCitySelect = (city) => {
    localStorage.setItem("homeCity", city.name);
    localStorage.setItem("homeCityState", city.state);
    localStorage.setItem("homeCityStateName", city.stateName);
    localStorage.setItem("homeCityData", JSON.stringify(city));
    setHomeCity(`${city.name}, ${city.state}`);
    
    setCityModalOpen(false);
    setCitySearchQuery("");
    setCitySearchResults([]);
    setShowCityDropdown(false);
    showSuccess(t('settings:cityUpdated', 'City updated!'));
    window.dispatchEvent(new Event("storage"));
  };

  const handleClearCity = () => {
    localStorage.removeItem("homeCity");
    localStorage.removeItem("homeCityState");
    localStorage.removeItem("homeCityStateName");
    localStorage.removeItem("homeCityData");
    setHomeCity(null);
    showSuccess(t('settings:cityCleared', 'City cleared!'));
    window.dispatchEvent(new Event("storage"));
  };

  const handleClearRegion = () => {
    if (homeCountry === 'us' && homeState) {
      localStorage.removeItem("homeState");
      localStorage.removeItem("homeCity");
      localStorage.removeItem("homeCityData");
      setHomeState(null);
      setHomeCity(null);
    } else if (homeCountry === 'ca' && homeProvince) {
      localStorage.removeItem("homeProvince");
      setHomeProvince(null);
    } else if (homeCountry === 'mx' && homeMexicanState) {
      localStorage.removeItem("homeMexicanState");
      setHomeMexicanState(null);
    } else if (homeCountry === 'uk' && homeUKRegion) {
      localStorage.removeItem("homeUKRegion");
      setHomeUKRegion(null);
    }
    showSuccess(t('settings:regionCleared', 'Region cleared!'));
    window.dispatchEvent(new Event("storage"));
  };

  // ============ HELPER FUNCTIONS ============
  const getCountryInfo = () => {
    return ALL_COUNTRIES.find(c => c.code === homeCountry) || null;
  };

  const getRegionInfo = () => {
    if (homeCountry === 'us' && homeState) {
      const state = states.find(s => s.key === homeState);
      return state ? { name: state.name, flag: state.flag } : null;
    }
    if (homeCountry === 'ca' && homeProvince) {
      const info = getProvincialInfo(homeProvince);
      return info ? { name: info.fullName, flag: info.flagImage } : null;
    }
    if (homeCountry === 'mx' && homeMexicanState) {
      const info = getMexicanStateInfo(homeMexicanState);
      return info ? { name: info.fullName, flag: info.flagImage } : null;
    }
    if (homeCountry === 'uk' && homeUKRegion) {
      const info = getUKRegionInfo(homeUKRegion);
      return info ? { name: info.fullName, flag: info.flagImage } : null;
    }
    return null;
  };

  const getRegionLabel = () => {
    switch (homeCountry) {
      case 'ca': return t('settings:regionProvince', 'Province/Territory');
      case 'mx': return t('settings:regionStateMx', 'State');
      case 'uk': return t('settings:regionUK', 'Region');
      case 'us':
      default: return t('settings:regionState', 'State');
    }
  };

  const getCurrentRegions = () => {
    switch (homeCountry) {
      case 'ca': return provinces;
      case 'mx': return mexicanStates;
      case 'uk': return ukRegions;
      case 'us':
      default: return states;
    }
  };

  const getFontSize = (name) => {
    if (!name) return "16px";
    if (name.length > 12) return "13px";
    if (name.length > 9) return "14px";
    return "16px";
  };

  // Render one theme tile. What counts as selected depends on Auto, because Auto
  // is what makes two picks meaningful at once:
  //   Auto on  -- both columns show a check. Both picks are live and the device
  //              decides between them; the column badge says which is showing.
  //   Auto off -- exactly one theme is in play, so only the resolved theme is
  //              checked. The other side's pick is still stored (turning Auto
  //              back on restores it), it just isn't a current selection.
  const renderThemeOption = (option, prefKey) => {
    const isPicked = themePrefs.isAuto
      ? themePrefs[prefKey] === option.id
      : activeTheme === option.id;
    const isGradient = option.preview.isGradient;

    return (
      <button
        key={option.id}
        className={`theme-option-compact ${isPicked ? 'selected' : ''}`}
        onClick={() => handleThemeSelect(option.id)}
        aria-pressed={isPicked}
        title={option.name}
      >
        <div 
          className="theme-preview-compact"
          style={{ 
            background: isGradient ? option.preview.bg : undefined,
            backgroundColor: !isGradient ? option.preview.bg : undefined
          }}
        >
          <div 
            className="preview-card-compact"
            style={{ 
              background: isGradient ? option.preview.card : undefined,
              backgroundColor: !isGradient ? option.preview.card : undefined
            }}
          >
            <div 
              className="preview-header-compact"
              style={{ backgroundColor: option.preview.accent }}
            />
          </div>
        </div>
        <span className="theme-name-compact">{option.name}</span>
        {isPicked && <CheckCircleFill className="check-icon" size={14} />}
      </button>
    );
  };

  const hasRegionSupport = FULL_SUPPORT_COUNTRIES.includes(homeCountry);
  // City is available wherever the app runs, not just the native builds. The
  // local page and its updates feed are on web now, and both need a city.
  const hasCitySupport = homeCountry === 'us';
  const countryInfo = getCountryInfo();
  const regionInfo = getRegionInfo();

  return (
    <div className="preferences-page">
      <Container style={{ maxWidth: "700px" }}>
        {/* No page header here at any width. The "Preferences" title is owned by
            the top bar (IntegratedSearchNavBar title mode, matched on
            /settings/location). The bar used to suppress its title on desktop
            because it carried a search input there; it no longer does, so a
            header on this page would duplicate it. */}

        {/* Section order is Location, Collection, Language, then Appearance.
            Location leads because it is the setting that changes what the app
            shows you rather than how it looks, and it is the one people come
            here to set; the route is /settings/location for the same reason.
            Collection follows it because Spending took its tab in the bottom
            bar, and this is now its most visible way in. Language sits with
            them as the other content-shaping choice, and the look-and-feel
            controls follow. */}

        {/* ============ LOCATION SECTION ============ */}
        <div className="preferences-section">
          <h2 className="section-title-compact">
            <GeoAlt className="section-icon" />
            {t('settings:location', 'Location')}
          </h2>

          <div className="location-row">
            {/* Country */}
            <div className="location-item">
              <span className="location-label">{t('settings:country', 'Country')}</span>
              <button
                onClick={() => setCountryModalOpen(true)}
                className="location-selector"
              >
                {countryInfo ? (
                  <>
                    <span className="location-flag">{countryInfo.flag}</span>
                    <span className="location-name">{countryInfo.name}</span>
                  </>
                ) : (
                  <span className="location-placeholder">{t('settings:select', 'Select')}</span>
                )}
                <PencilSquare size={12} className="edit-icon" />
              </button>
            </div>

            {/* Region */}
            {hasRegionSupport && (
              <div className="location-item">
                <span className="location-label">{getRegionLabel()}</span>
                <button
                  onClick={() => setRegionModalOpen(true)}
                  className="location-selector"
                >
                  {regionInfo ? (
                    <>
                      <img 
                        src={regionInfo.flag} 
                        alt="" 
                        className="location-flag-img"
                      />
                      <span className="location-name">{regionInfo.name}</span>
                      <XCircle 
                        size={12} 
                        className="clear-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearRegion();
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <span className="location-placeholder">{t('settings:select', 'Select')}</span>
                      <PencilSquare size={12} className="edit-icon" />
                    </>
                  )}
                </button>
              </div>
            )}

            {/* City */}
            {hasCitySupport && (
              <div className="location-item">
                <span className="location-label">{t('settings:city', 'City')}</span>
                <button
                  onClick={() => setCityModalOpen(true)}
                  className="location-selector"
                >
                  {homeCity ? (
                    <>
                      <span className="location-name">{homeCity}</span>
                      <XCircle 
                        size={12} 
                        className="clear-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearCity();
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <span className="location-placeholder">{t('settings:select', 'Select')}</span>
                      <PencilSquare size={12} className="edit-icon" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {homeCountry && !hasRegionSupport && (
            <div className="info-message-compact">
              {t('settings:regionComingSoon', 'Regional support for {{country}} coming soon!', { country: countryInfo?.name })}
            </div>
          )}
        </div>

        {/* ============ COLLECTION SECTION ============ */}
        {/* The saved collection lost its bottom-bar tab to Spending; it is still
            in the menu, and here it is one tap from settings as well. Same row
            style as the language and font options, with a chevron because it
            opens a page rather than choosing a value. */}
        <div className="preferences-section">
          <h2 className="section-title-compact">
            <Folder className="section-icon" />
            {t('settings:collection', 'Collection')}
          </h2>
          <button
            type="button"
            className="font-option-compact collection-link"
            onClick={() => navigate('/collection')}
          >
            <span className="font-name-compact">
              {t('settings:viewSavedCollection', 'View saved collection')}
            </span>
            <ChevronRight size={14} className="collection-link-chevron" aria-hidden="true" />
          </button>
        </div>

        {/* ============ LANGUAGE SECTION ============ */}
        <div className="preferences-section">
          <h2 className="section-title-compact">
            <Globe2 className="section-icon" />
            {t('settings:language', 'Language')}
          </h2>
          <div className="font-list-compact">
            {ALL_LANGUAGES.map((langKey) => {
              const info = LANGUAGE_INFO[langKey];
              const isSelected = selectedLanguage === langKey;

              return (
                <button
                  key={langKey}
                  className={`font-option-compact ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleLanguageChange(langKey)}
                  aria-pressed={isSelected}
                >
                  <span className="font-name-compact">{info.name}</span>
                  {isSelected && <CheckCircleFill className="check-icon" size={14} />}
                </button>
              );
            })}
          </div>
        </div>

        {/* ============ APPEARANCE & FONT ROW ============ */}
        <div className="appearance-font-row">
          {/* Theme Section */}
          <div className="preferences-section compact-section">
            <h2 className="section-title-compact">
              <Palette className="section-icon" />
              {t('settings:appearance', 'Appearance')}
            </h2>

            {/* Auto is a switch, not a theme. With it on, the device decides
                which column is showing; either way both picks stay editable. */}
            <div className="auto-theme-row">
              <div className="auto-theme-copy">
                <span className="auto-theme-label">
                  {t('settings:autoTheme', 'Auto')}
                </span>
          
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={themePrefs.isAuto}
                aria-label={t('settings:autoTheme', 'Auto')}
                className={`theme-switch ${themePrefs.isAuto ? 'on' : ''}`}
                onClick={() => handleAutoToggle(!themePrefs.isAuto)}
              >
                <span className="theme-switch-knob" />
              </button>
            </div>

            <div className="theme-columns">
              {THEME_COLUMNS.map((column) => (
                <div
                  key={column.side}
                  className={`theme-column ${activeSide === column.side ? 'active' : ''}`}
                >
                  <div className="theme-column-title">
                    {column.icon}
                    <span>{t(column.labelKey, column.labelFallback)}</span>
                    {/* Only under Auto. With Auto off the single check already
                        says which theme is showing, so the badge would just be
                        repeating it. */}
                    {themePrefs.isAuto && activeSide === column.side && (
                      <span className="theme-column-badge">
                        {t('settings:themeInUse', 'In use')}
                      </span>
                    )}
                  </div>
                  <div className="theme-column-options">
                    {column.options.map((option) => renderThemeOption(option, column.prefKey))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Font Section */}
          <div className="preferences-section compact-section">
            <h2 className="section-title-compact">
              <Fonts className="section-icon" />
              {t('settings:font', 'Font')}
            </h2>
            <div className="font-list-compact">
              {ALL_FONTS.map((fontKey) => {
                const info = FONT_INFO[fontKey];
                const isSelected = selectedFont === fontKey;

                return (
                  <button
                    key={fontKey}
                    className={`font-option-compact ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleFontChange(fontKey)}
                    aria-pressed={isSelected}
                  >
                    <span className={`font-name-compact font-preview-${fontKey}`}>
                      {info.name}
                    </span>
                    {isSelected && <CheckCircleFill className="check-icon" size={14} />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </Container>

      {/* ============ MODALS ============ */}
      
      {/* Country Modal */}
      <Modal
        show={countryModalOpen}
        onHide={() => setCountryModalOpen(false)}
        size="lg"
        centered
        className="preferences-modal"
      >
        <Modal.Header className="preferences-modal-header" closeButton>
          <h3><Globe2 className="me-2" />{t('settings:selectCountry', 'Select a Country')}</h3>
        </Modal.Header>
        <Modal.Body className="preferences-modal-body">
          <div className="modal-content-inner">
            <div className="options-grid">
              {ALL_COUNTRIES.map((country) => (
                <button
                  key={country.code}
                  onClick={() => handleCountrySelect(country.code)}
                  className={`option-button ${homeCountry === country.code ? 'selected' : ''}`}
                >
                  <span style={{ fontSize: "20px" }}>{country.flag}</span>
                  {country.name}
                </button>
              ))}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Region Modal */}
      <Modal
        show={regionModalOpen}
        onHide={() => setRegionModalOpen(false)}
        size="xl"
        centered
        className="preferences-modal"
      >
        <Modal.Header className="preferences-modal-header" closeButton>
          <h3><GeoAlt className="me-2" />{t('settings:selectRegion', 'Select a {{label}}', { label: getRegionLabel() })}</h3>
        </Modal.Header>
        <Modal.Body className="preferences-modal-body">
          <div className="modal-content-inner">
            <div className="options-grid options-grid-scroll">
              {getCurrentRegions().map((region) => {
                const isSelected = 
                  (homeCountry === 'us' && homeState === region.key) ||
                  (homeCountry === 'ca' && homeProvince === region.key) ||
                  (homeCountry === 'mx' && homeMexicanState === region.key) ||
                  (homeCountry === 'uk' && homeUKRegion === region.key);
                
                return (
                  <button
                    key={region.key}
                    onClick={() => {
                      if (homeCountry === 'us') handleStateSelect(region.key);
                      else if (homeCountry === 'ca') handleProvinceSelect(region.key);
                      else if (homeCountry === 'mx') handleMexicanStateSelect(region.key);
                      else if (homeCountry === 'uk') handleUKRegionSelect(region.key);
                    }}
                    className={`option-button ${isSelected ? 'selected' : ''}`}
                    style={{ fontSize: getFontSize(region.name) }}
                  >
                    <img
                      src={region.flag}
                      alt=""
                      style={{ height: "20px", objectFit: "contain" }}
                    />
                    {region.name}
                  </button>
                );
              })}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* City Modal */}
      <Modal
        show={cityModalOpen}
        onHide={() => {
          setCityModalOpen(false);
          setCitySearchQuery("");
          setCitySearchResults([]);
          setShowCityDropdown(false);
        }}
        size="lg"
        centered
        className="preferences-modal"
      >
        <Modal.Header className="preferences-modal-header" closeButton>
          <h3><Building className="me-2" />{t('settings:findCity', 'Find Your City')}</h3>
        </Modal.Header>
        <Modal.Body className="preferences-modal-body">
          <div className="modal-content-inner" style={{ minHeight: "400px" }}>
            <div style={{ position: "relative", marginBottom: "20px" }}>
              <input
                type="text"
                value={citySearchQuery}
                onChange={handleCitySearchChange}
                onFocus={() => citySearchResults.length > 0 && setShowCityDropdown(true)}
                placeholder={t('settings:cityPlaceholder', 'Type your city name...')}
                className={`search-input ${showCityDropdown ? 'dropdown-open' : ''}`}
              />
              
              {searchingCities && (
                <div className="search-loading">{t('settings:searching', 'Searching...')}</div>
              )}

              {showCityDropdown && !searchingCities && citySearchResults.length > 0 && (
                <div className="search-dropdown">
                  {citySearchResults.map((city, index) => (
                    <button
                      key={`${city.name}_${city.state}_${index}`}
                      onClick={() => handleCitySelect(city)}
                      className="dropdown-item"
                    >
                      <span>
                        <span style={{ fontWeight: "500" }}>{city.name}</span>
                        <span style={{ opacity: 0.7 }}>, {city.stateName}</span>
                      </span>
                      <span style={{ fontSize: "12px", opacity: 0.5 }}>{city.type}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {searchError && (
              <div className="error-message">{searchError}</div>
            )}

            <div className="helper-text">
              {citySearchQuery.length < 2 ? (
                <p>{t('settings:cityHelpStart', 'Start typing to search for your city')}</p>
              ) : !searchingCities && citySearchResults.length === 0 && citySearchQuery.length >= 2 ? (
                <p>{t('settings:cityNoResults', 'No cities found matching "{{query}}"', { query: citySearchQuery })}</p>
              ) : null}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <style>{`
        /* ============ BASE STYLES ============ */
        /* No background of its own. theme.css paints the page on html, body
           and #root -- Classic Light's gradient, every other theme's flat
           color -- for the full height of the document, footer included. A
           copy here stopped at the page gutters and, in Classic Light,
           restarted the gradient at the top of this page, so it drew a box
           whose edges did not meet the footer. */
        .preferences-page {
          min-height: 100vh;
          color: var(--text-primary, white);
          padding-top: 8px;
          padding-bottom: 100px;
        }

        /* ============ SECTIONS ============ */
        .preferences-section {
          background: var(--bg-card, rgba(1, 108, 174, 0.15));
          border-radius: 16px;
          padding: 16px;
          margin-bottom: 16px;
        }

        .section-title-compact {
          font-size: 15px;
          font-weight: 600;
          margin: 0 0 12px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .section-icon {
          color: var(--accent-primary, #016cae);
          flex-shrink: 0;
        }

        /* ============ COLLECTION LINK ============ */
        /* A full-width row in the option style; the chevron marks it as a way
           to another page rather than a choice. */
        .collection-link {
          width: 100%;
          text-align: left;
        }

        .collection-link-chevron {
          color: var(--text-tertiary);
          flex-shrink: 0;
        }

        /* ============ APPEARANCE & FONT ROW ============ */
        /* This row is last in the page now, so its own margin-bottom is what
           closes the stack rather than separating it from a following section.
           It stays for the padding-bottom on .preferences-page to add to. */
        .appearance-font-row {
          display: flex;
          gap: 16px;
          margin-bottom: 16px;
        }

        .appearance-font-row > .preferences-section {
          flex: 1;
          min-width: 0;
        }

        /* ============ AUTO SWITCH ============ */
        .auto-theme-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 14px;
        }

        .auto-theme-copy {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }

        .auto-theme-label {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-primary);
        }

        .auto-theme-hint {
          font-size: 12px;
          line-height: 1.35;
          color: var(--text-tertiary);
        }

        .theme-switch {
          flex-shrink: 0;
          width: 46px;
          height: 26px;
          padding: 3px;
          border-radius: 13px;
          border: none;
          cursor: pointer;
          background: var(--bg-button-secondary, rgba(1, 108, 174, 0.2));
          transition: background 0.2s ease;
        }

        .theme-switch.on {
          background: var(--accent-primary);
        }

        .theme-switch-knob {
          display: block;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #ffffff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
          transition: transform 0.2s ease;
        }

        .theme-switch.on .theme-switch-knob {
          transform: translateX(20px);
        }

        /* ============ THEME COLUMNS ============ */
        .theme-columns {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .theme-column {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .theme-column-title {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          color: var(--text-secondary);
        }

        .theme-column-title svg {
          color: var(--accent-primary, #016cae);
          flex-shrink: 0;
        }

        .theme-column-badge {
          margin-left: auto;
          padding: 1px 6px;
          border-radius: 999px;
          background: var(--bg-button-accent, rgba(1, 108, 174, 0.4));
          color: var(--text-primary);
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0;
          text-transform: none;
          white-space: nowrap;
        }

        .theme-column-options {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        /* ============ THEME OPTIONS (COMPACT) ============ */
        .theme-option-compact {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 10px 8px;
          background: var(--bg-button-secondary, rgba(1, 108, 174, 0.2));
          border: 2px solid transparent;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
        }

        .theme-option-compact:hover {
          background: var(--bg-card-hover, rgba(1, 108, 174, 0.3));
        }

        /* The border alone was getting lost against the selected background, so
           the ring is drawn with box-shadow as well -- it sits outside the
           border box and doesn't shift the tile's size the way an extra border
           width would. Fonts and languages get the same treatment below. */
        .theme-option-compact.selected,
        .font-option-compact.selected {
          background: var(--bg-button-accent, rgba(1, 108, 174, 0.4));
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 1px var(--accent-primary);
        }

        .theme-preview-compact {
          width: 100%;
          height: 32px;
          border-radius: 6px;
          border: 1px solid var(--border-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 6px;
          overflow: hidden;
        }

        .preview-card-compact {
          width: 36px;
          height: 24px;
          border-radius: 4px;
          overflow: hidden;
        }

        .preview-header-compact {
          height: 6px;
          width: 100%;
        }

        .theme-name-compact {
          font-size: 12px;
          font-weight: 500;
          color: var(--text-primary, white);
        }

        .check-icon {
          position: absolute;
          top: 6px;
          right: 6px;
          color: var(--accent-primary);
        }

        /* ============ FONT OPTIONS (COMPACT) ============ */
        .font-list-compact {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .font-option-compact {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 14px;
          background: var(--bg-button-secondary, rgba(1, 108, 174, 0.2));
          border: 2px solid transparent;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
        }

        .font-option-compact:hover {
          background: var(--bg-card-hover, rgba(1, 108, 174, 0.3));
        }

        .font-name-compact {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-primary, white);
        }

        .font-list-compact .font-option-compact .font-name-compact.font-preview-system {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
        }
        .font-list-compact .font-option-compact .font-name-compact.font-preview-verdana {
          font-family: Verdana, Geneva, sans-serif !important;
        }
        .font-list-compact .font-option-compact .font-name-compact.font-preview-atkinson {
          font-family: "Atkinson Hyperlegible", -apple-system, BlinkMacSystemFont, sans-serif !important;
        }

        /* ============ LOCATION OPTIONS (COMPACT) ============ */
        .location-row {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .location-item {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .location-label {
          font-size: 13px;
          color: var(--text-secondary);
          white-space: nowrap;
        }

        .location-selector {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          background: var(--bg-button-secondary, rgba(1, 108, 174, 0.2));
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.2s ease;
          font-size: 13px;
          color: var(--text-primary);
        }

        .location-selector:hover {
          background: var(--bg-button-hover, rgba(1, 108, 174, 0.3));
        }

        .location-flag {
          font-size: 16px;
          line-height: 1;
        }

        .location-flag-img {
          height: 14px;
          object-fit: contain;
        }

        .location-name {
          font-weight: 500;
          max-width: 120px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .location-placeholder {
          color: var(--text-tertiary);
        }

        .edit-icon {
          color: var(--text-tertiary);
          flex-shrink: 0;
        }

        .clear-icon {
          color: var(--text-tertiary);
          flex-shrink: 0;
          transition: color 0.2s ease;
        }

        .clear-icon:hover {
          color: var(--color-error, #dc3545);
        }

        .info-message-compact {
          background-color: var(--bg-button-secondary, rgba(1, 108, 174, 0.2));
          padding: 10px 14px;
          border-radius: 10px;
          color: var(--text-secondary);
          font-size: 12px;
          text-align: center;
          margin-top: 12px;
        }

        /* ============ MODAL STYLES ============ */
        .preferences-modal .modal-content {
          background-color: var(--bg-primary);
          border: 1px solid var(--border-primary);
        }

        .preferences-modal-header {
          background-color: var(--bg-secondary);
          color: var(--text-primary);
          border-color: var(--border-primary);
        }

        .preferences-modal-header h3 {
          margin: 0;
          font-size: 18px;
          display: flex;
          align-items: center;
        }

        .preferences-modal-body {
          padding: 0;
          background-color: var(--bg-primary);
        }

        .modal-content-inner {
          padding: 20px;
        }

        .options-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }

        .options-grid-scroll {
          max-height: 60vh;
          overflow-y: auto;
        }

        .option-button {
          font-size: 15px;
          padding: 12px 10px;
          background-color: var(--bg-button-secondary, rgba(1, 108, 174, 0.2));
          color: var(--text-primary);
          border: none;
          border-radius: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: 48px;
          transition: background-color 0.2s ease;
        }

        .option-button:hover {
          background-color: var(--bg-button-hover);
        }

        .option-button.selected {
          background-color: var(--accent-secondary, #016cae);
        }

        /* ============ SEARCH STYLES ============ */
        .search-input {
          width: 100%;
          padding: 14px 18px;
          font-size: 15px;
          background-color: var(--bg-tertiary);
          color: var(--text-primary);
          border: 1px solid var(--border-primary);
          border-radius: 12px;
          outline: none;
          transition: all 0.2s ease;
        }

        .search-input::placeholder {
          color: var(--text-muted);
        }

        .search-input:focus {
          border-color: var(--accent-primary);
        }

        .search-input.dropdown-open {
          border-radius: 12px 12px 0 0;
        }

        .search-loading {
          position: absolute;
          right: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-muted);
          font-size: 13px;
        }

        .search-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          max-height: 280px;
          overflow-y: auto;
          background-color: var(--bg-tertiary);
          border: 1px solid var(--border-primary);
          border-top: none;
          border-radius: 0 0 12px 12px;
          z-index: 100;
          box-shadow: 0 4px 12px var(--shadow-color);
        }

        .dropdown-item {
          width: 100%;
          padding: 12px 18px;
          font-size: 14px;
          background-color: transparent;
          color: var(--text-primary);
          border: none;
          border-bottom: 1px solid var(--border-secondary);
          cursor: pointer;
          text-align: left;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: background-color 0.2s ease;
        }

        .dropdown-item:last-child {
          border-bottom: none;
        }

        .dropdown-item:hover {
          background-color: var(--bg-card-hover);
        }

        .error-message {
          margin-bottom: 15px;
          padding: 10px;
          background-color: var(--color-error-bg);
          border: 1px solid var(--color-error-border);
          border-radius: 10px;
          text-align: center;
          color: var(--color-error);
          font-size: 13px;
        }

        .helper-text {
          text-align: center;
          color: var(--text-tertiary);
          font-size: 13px;
        }
      `}</style>
    </div>
  );
};

export default PreferencesPage;